'use client';

import React, { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { RestrictedAccessGate } from '@/components/common/RestrictedAccessGate';
import {
  Users,
  Search,
  Filter,
  CheckCircle2,
  Building2,
  MapPin,
  GraduationCap,
  Briefcase,
  Mail,
  Award,
  Sparkles,
  Calendar,
  Lock,
} from 'lucide-react';

interface AlumniItem {
  id: string;
  nim: string;
  name: string;
  batch: number;
  graduationYear: number;
  cohortName: string;
  jobTitle: string;
  company: string;
  industry: string;
  city: string;
  verified: boolean;
  avatarText: string;
  skills: string[];
  email: string;
  linkedin?: string;
  bio: string;
  member_number?: string;
}

export default function AlumniDirectoryPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const [alumniList, setAlumniList] = useState<AlumniItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBatch, setSelectedBatch] = useState<string>('all');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');
  const [selectedAlumni, setSelectedAlumni] = useState<AlumniItem | null>(null);

  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        setIsLoadingList(true);
        const res: any = await api.get('/alumni');
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          setAlumniList(res.data);
          return;
        }

        // Fallback: check localStorage for any pending/verified users
        if (typeof window !== 'undefined') {
          const savedPending = localStorage.getItem('ika-mesin-pending-verifications');
          if (savedPending) {
            try {
              const parsed = JSON.parse(savedPending);
              if (Array.isArray(parsed) && parsed.length > 0) {
                const mapped: AlumniItem[] = parsed.map((p: any) => {
                  const name = p.name || 'Alumni Mesin';
                  const initials = name
                    .split(' ')
                    .filter((part: string) => !['S.T.', 'M.T.', 'Dr.', 'Ir.', 'H.'].includes(part))
                    .map((part: string) => part[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase();

                  return {
                    id: String(p.id),
                    nim: p.nim || '-',
                    name: p.name,
                    batch: Number(p.entryYear) || 2021,
                    graduationYear: Number(p.graduationYear) || 2025,
                    cohortName: `Angkatan ${p.entryYear || 2021}`,
                    jobTitle: 'Alumni Teknik Mesin',
                    company: 'Teknik Mesin ITN Malang',
                    industry: 'Rekayasa & Manufaktur',
                    city: 'Malang',
                    verified: true,
                    avatarText: initials || 'AM',
                    skills: ['Solidarity Forever', 'Teknik Mesin'],
                    email: p.email || '',
                    bio: 'Alumni resmi Ikatan Keluarga Alumni Teknik Mesin ITN Malang.',
                  };
                });
                setAlumniList(mapped);
                return;
              }
            } catch (e) {}
          }
        }
      } catch (err) {
        console.warn('Notice loading alumni:', err);
      } finally {
        setIsLoadingList(false);
      }
    };

    fetchAlumni();
  }, []);

  const batches = useMemo(() => {
    const list = Array.from(new Set(alumniList.map((a) => a.batch))).sort((a, b) => b - a);
    return list;
  }, [alumniList]);

  const industries = useMemo(() => {
    const list = Array.from(new Set(alumniList.map((a) => a.industry))).sort();
    return list;
  }, [alumniList]);

  const filteredAlumni = useMemo(() => {
    return alumniList.filter((item) => {
      const matchSearch =
        searchQuery === '' ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.nim.includes(searchQuery) ||
        (item.member_number && item.member_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
        item.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.skills.some((s) => s.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchBatch = selectedBatch === 'all' || item.batch.toString() === selectedBatch;
      const matchIndustry = selectedIndustry === 'all' || item.industry === selectedIndustry;

      return matchSearch && matchBatch && matchIndustry;
    });
  }, [alumniList, searchQuery, selectedBatch, selectedIndustry]);

  return (
    <div className="flex min-h-screen flex-col bg-background selection:bg-[#D71920] selection:text-white">
      <Navbar />

      <main className="flex-1 pb-20">
        {/* Header Section */}
        <section className="relative border-b-4 border-[#D71920] bg-[linear-gradient(135deg,#021024_0%,#062347_35%,#004A99_75%,#0066CC_100%)] py-12 lg:py-14 text-white overflow-hidden shadow-lg">
          {/* Crisp Engineering Blueprint Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:32px_32px] opacity-65 pointer-events-none" />
          {/* Luminous Accents */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[radial-gradient(circle_at_80%_20%,rgba(215,25,32,0.28),transparent_70%)] pointer-events-none" />
          <div className="absolute -bottom-10 left-1/4 w-80 h-80 bg-[radial-gradient(circle,rgba(0,102,204,0.35),transparent_70%)] pointer-events-none" />
          <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/20 px-3.5 py-1 text-xs font-bold text-blue-200">
                <Users className="h-3.5 w-3.5 text-red-400" />
                <span>DATABASE RESMI ALUMNI TEKNIK MESIN ITN MALANG</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                Direktori Alumni Lintas Angkatan
              </h1>
              <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
                Temukan rekan satu almamater Teknik Mesin ITN Malang di seluruh Indonesia dan mancanegara. Bangun jejaring profesional, diskusikan peluang industri, dan pererat solidaritas Arek Mesin.
              </p>
            </div>
          </div>
        </section>

        {/* Option C: Access Control Gate */}
        {!isAuthenticated ? (
          <RestrictedAccessGate
            title="Direktori Alumni Terkunci"
            subtitle="Demi perlindungan privasi data pribadi dan keamanan direktori, halaman ini hanya dapat diakses oleh Alumni Teknik Mesin ITN Malang yang telah terdaftar dan login ke akun resmi."
            featureName="Direktori Alumni"
            redirectUrl="/alumni"
          />
        ) : (
          <>
            {/* Filter and Search Bar for Authenticated Users */}
            <section className="border-b border-[#E2E8F0] dark:border-slate-800 bg-white dark:bg-[#0A2544] py-6 shadow-xs">
              <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2 relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
                    <Input
                      type="text"
                      placeholder="Cari nama alumni, NIM, perusahaan, posisi, atau keahlian..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 rounded-lg h-10 text-sm border-[#E2E8F0] dark:border-slate-700 focus-visible:ring-[#004A99]"
                    />
                  </div>

                  <div>
                    <select
                      value={selectedBatch}
                      onChange={(e) => setSelectedBatch(e.target.value)}
                      className="w-full h-10 px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-[#1F2937] dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#004A99]"
                    >
                      <option value="all">Semua Angkatan</option>
                      {batches.map((b) => (
                        <option key={b} value={b.toString()}>
                          Angkatan {b}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <select
                      value={selectedIndustry}
                      onChange={(e) => setSelectedIndustry(e.target.value)}
                      className="w-full h-10 px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-[#1F2937] dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#004A99] truncate"
                    >
                      <option value="all">Semua Industri</option>
                      {industries.map((ind) => (
                        <option key={ind} value={ind}>
                          {ind}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-xs text-[#64748B] dark:text-slate-400">
                  <span>Menampilkan {filteredAlumni.length} profil alumni terverifikasi</span>
                  {(searchQuery || selectedBatch !== 'all' || selectedIndustry !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedBatch('all');
                        setSelectedIndustry('all');
                      }}
                      className="text-[#D71920] hover:underline font-bold"
                    >
                      Reset Filter
                    </button>
                  )}
                </div>
              </div>
            </section>

            {/* Alumni Grid */}
            <section className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-8">
          {filteredAlumni.length === 0 ? (
            <div className="text-center py-16 bg-white dark:bg-[#0A2544] rounded-xl border border-dashed border-[#E2E8F0] dark:border-slate-800 p-8 shadow-xs">
              <Users className="h-10 w-10 text-[#64748B] mx-auto mb-3" />
              <h3 className="text-sm font-bold text-[#082B52] dark:text-white">Tidak Ada Alumni Ditemukan</h3>
              <p className="text-xs text-[#64748B] dark:text-slate-400 mt-1 max-w-sm mx-auto">
                Coba ubah kata kunci pencarian atau reset filter angkatan dan industri.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {filteredAlumni.map((alumni) => (
                <Card
                  key={alumni.id}
                  className="group border border-[#E2E8F0] dark:border-slate-800 bg-white dark:bg-[#0A2544] card-corporate shadow-corporate rounded-xl flex flex-col justify-between overflow-hidden relative"
                >
                  {/* Card top indicator: primary corporate blue */}
                  <div className="h-1 w-full bg-[#004A99]" />
                  <CardContent className="p-5 space-y-3.5">
                    {/* Header: Avatar, Name, Batch */}
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/60 text-[#004A99] dark:text-blue-300 font-bold text-sm border border-blue-100 dark:border-blue-900/40">
                        {alumni.avatarText}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-[#082B52] dark:text-white truncate group-hover:text-[#004A99] transition-colors leading-tight">
                          {alumni.name}
                        </h3>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[11px] font-bold text-[#004A99] dark:text-blue-400">
                            Angkatan {alumni.batch}
                          </span>
                          {alumni.verified && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300">
                              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5 text-emerald-600" />
                              Sah
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-[#64748B] dark:text-slate-400 mt-0.5">
                          NIM: {alumni.nim}
                          {alumni.member_number ? (
                            <span className="ml-1.5 font-mono text-[#D71920] dark:text-red-400 font-semibold">
                              • {alumni.member_number}
                            </span>
                          ) : null}
                        </p>
                      </div>
                    </div>

                    {/* Job & Company */}
                    <div className="space-y-1.5 py-2.5 border-y border-[#E2E8F0] dark:border-slate-800 text-xs">
                      <div className="flex items-center gap-2 text-[#1F2937] dark:text-slate-200 font-medium">
                        <Briefcase className="h-3.5 w-3.5 text-[#D71920] shrink-0" />
                        <span className="truncate">{alumni.jobTitle}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#64748B] dark:text-slate-400">
                        <Building2 className="h-3.5 w-3.5 text-[#004A99] shrink-0" />
                        <span className="truncate">{alumni.company}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#64748B] dark:text-slate-400 text-[11px]">
                        <MapPin className="h-3.5 w-3.5 text-[#94A3B8] shrink-0" />
                        <span className="truncate">{alumni.city}</span>
                      </div>
                    </div>

                    {/* Top Skills Tags */}
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {alumni.skills.slice(0, 3).map((skill, sIdx) => (
                        <span
                          key={sIdx}
                          className="text-[10px] font-semibold bg-[#F5F7FA] dark:bg-slate-800 text-[#082B52] dark:text-slate-300 border border-[#E2E8F0] dark:border-slate-700 px-2 py-0.5 rounded-md"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </CardContent>

                  {/* Card Footer: Detail Button */}
                  <div className="px-5 pb-5 pt-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedAlumni(alumni)}
                      className="w-full rounded-lg text-xs font-bold text-[#004A99] border-[#004A99]/30 hover:bg-[#004A99] hover:text-white transition-colors h-9"
                    >
                      Lihat Profil Lengkap
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
        </>
      )}
      </main>

      {/* Alumni Detail Modal */}
      {selectedAlumni && (
        <Dialog open={!!selectedAlumni} onOpenChange={(open) => !open && setSelectedAlumni(null)}>
          <DialogContent className="sm:max-w-xl rounded-xl p-6 border border-[#E2E8F0] dark:border-slate-800 bg-white dark:bg-[#0A2544]">
            <DialogHeader className="space-y-2 text-left border-b border-[#E2E8F0] dark:border-slate-800 pb-4">
              <div className="flex items-center gap-3.5">
                <div className="flex h-13 w-13 items-center justify-center rounded-lg bg-[#004A99] text-white font-bold text-lg shadow-xs">
                  {selectedAlumni.avatarText}
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-[#082B52] dark:text-white">
                    {selectedAlumni.name}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-[#64748B] dark:text-slate-400 flex flex-wrap items-center gap-2 mt-0.5">
                    <span>NIM: {selectedAlumni.nim}</span>
                    <span>•</span>
                    <span className="text-[#D71920] font-bold">
                      Angkatan {selectedAlumni.batch} ({selectedAlumni.cohortName})
                    </span>
                    {selectedAlumni.member_number && (
                      <>
                        <span>•</span>
                        <span className="font-mono font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded">
                          ID: {selectedAlumni.member_number}
                        </span>
                      </>
                    )}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-3 text-sm">
              <div className="p-3.5 bg-[#F5F7FA] dark:bg-slate-900/60 border border-[#E2E8F0] dark:border-slate-800 rounded-lg space-y-1">
                <h4 className="text-[11px] font-bold text-[#082B52] dark:text-slate-300 uppercase tracking-wider">
                  Ringkasan Profil Alumni
                </h4>
                <p className="text-xs text-[#1F2937] dark:text-slate-300 leading-relaxed">{selectedAlumni.bio}</p>
              </div>

              <div className="grid grid-cols-2 gap-3.5 p-3 rounded-lg border border-[#E2E8F0] dark:border-slate-800">
                <div className="space-y-0.5">
                  <span className="text-[11px] text-[#64748B] dark:text-slate-400">No. Anggota / ID</span>
                  <p className="font-bold font-mono text-[#D71920] dark:text-red-400 text-xs">
                    {selectedAlumni.member_number || '-'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[11px] text-[#64748B] dark:text-slate-400">Posisi / Jabatan</span>
                  <p className="font-bold text-[#082B52] dark:text-white text-xs">{selectedAlumni.jobTitle}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[11px] text-[#64748B] dark:text-slate-400">Perusahaan</span>
                  <p className="font-bold text-[#082B52] dark:text-white text-xs">{selectedAlumni.company}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[11px] text-[#64748B] dark:text-slate-400">Sektor Industri</span>
                  <p className="font-bold text-[#082B52] dark:text-white text-xs">{selectedAlumni.industry}</p>
                </div>
                <div className="space-y-0.5 col-span-2">
                  <span className="text-[11px] text-[#64748B] dark:text-slate-400">Kota Domisili</span>
                  <p className="font-bold text-[#082B52] dark:text-white text-xs">{selectedAlumni.city}</p>
                </div>
              </div>

              {/* Skills */}
              <div className="space-y-1.5">
                <span className="text-xs font-bold text-[#082B52] dark:text-white">Keahlian & Bidang Spesialisasi</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedAlumni.skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs rounded-md bg-blue-50 text-[#004A99] dark:bg-blue-950/60 dark:text-blue-300 border border-blue-100 dark:border-blue-900/40">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-2 pt-2 border-t border-[#E2E8F0] dark:border-slate-800">
                <span className="text-xs font-bold text-[#082B52] dark:text-white">Jejaring & Kontak Profesional</span>
                <div className="flex flex-col gap-2 text-xs">
                  <div className="flex items-center gap-2 text-[#64748B] dark:text-slate-300">
                    <Mail className="h-4 w-4 text-[#D71920] shrink-0" />
                    <span>{selectedAlumni.email}</span>
                  </div>
                  {selectedAlumni.linkedin && (
                    <div className="flex items-center gap-2 text-[#64748B] dark:text-slate-300">
                      <svg className="h-4 w-4 fill-[#004A99] shrink-0" viewBox="0 0 24 24">
                        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.6a1.64 1.64 0 0 0-1.64 1.63c0 .91.73 1.64 1.64 1.64s1.63-.73 1.63-1.64c0-.9-.73-1.63-1.63-1.63"/>
                      </svg>
                      <span>{selectedAlumni.linkedin}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t border-[#E2E8F0] dark:border-slate-800">
              <Button
                variant="outline"
                className="rounded-lg text-xs font-bold"
                onClick={() => setSelectedAlumni(null)}
              >
                Tutup
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Footer />
    </div>
  );
}
